import {
  brandDiscountCents, brandDiscountFromRow, brandNetLines, creatorShareLines, dealBpForMonth, effectiveCommissionPercent, extraInPeriod, resolveCommissionRate,
  shareOfHonorar, tiersFromJson, type BrandDiscount, type CommissionTier,
} from "@talentkontor/domain";
import { money as geldAnzeige, monthLabel as monatAnzeige, orgLocale, numberText } from "../../../lib/format";
import { inLang } from "../../../lib/sprache";

/** Positionstexte gehören zum Beleg: deutsche Monatsnamen und Zahlenformat der Agentur, unabhängig von der Oberflächensprache. */
const money = (cents: number | null | undefined, currency: string): string => geldAnzeige(cents, currency, orgLocale());
const monthLabel = (key: string): string => inLang("de", () => monatAnzeige(key));

/**
 * Vorbelegung des Rechnungsgenerators aus Kooperation und Monat (Korrektur 0075, GELD-2/3/4/6). Rein, damit Rechnung an die
 * Marke, Gutschrift, Provisionsrechnung und Sammelrechnung mit denselben Fachregeln rechnen wie Aufteilung, Anforderung und Wächter:
 *   Zusatzpositionen   mit Monat in diesem Monat, ohne Monat („Ganze Kooperation“) nur im ersten Monat (extraInPeriod)
 *   Staffel            Provision und Creator-Anteil nach der Staffel der Kooperation (shareOfHonorar)
 *   Abzug der Marke    eigene Minuszeile auf der Rechnung an die Marke; Creator-Anteil und Provision nach dem Abzug
 *   Direktweg          die Rechnung der Agentur an die Marke trägt nur die Agenturgebühr (das Honorar stellt der Creator)
 */

export type VorbelegungsArt = "agency_to_brand" | "agency_to_talent" | "talent_to_brand" | "self_billing";

export interface VorbelegungsZeile {
  description: string;
  amountCents: number;
  commissionFree: boolean;
}

interface DealLike {
  id: string;
  title: string;
  model: string;
  months: number | null;
  budget_cents: number | null;
  deliverables: string | null;
  currency: string;
  commission_bp: number | null;
  commission_tiers?: unknown;
  brand_discount_bp?: number | null;
  brand_discount_bearer?: string | null;
  post_term?: unknown;
  post_term_from?: string | null;
  post_term_until?: string | null;
}
interface PeriodLike { id: string; month: string; amount_cents: number | null; commission_forecast_cents?: number | null; status?: string | null; cancellation_fee_cents?: number | null; original_amount_cents?: number | null }
interface ItemLike { deal_id: string; period_id: string | null; description: string; amount_cents: number; commissionable: boolean }
interface FeeLike { agency_fee_mode?: string | null; agency_fee_bp?: number | null; agency_fee_cents?: number | null }

export interface VorbelegungsInput {
  kind: VorbelegungsArt;
  deal: DealLike;
  /** Gewählter Monat der Kooperation; null = ohne Monat (Kooperation ohne Monate) */
  period: PeriodLike | null;
  /** Monate derselben Kooperation (für „erster Monat“) */
  periods: readonly PeriodLike[];
  items: readonly ItemLike[];
  terms?: FeeLike | null;
  settings: FeeLike & { default_commission_bp?: number | null };
  talentCommissionBp: number | null;
  /** Abrechnungsweg „Creator direkt an die Marke“ */
  direct: boolean;
  /** Gewählter Monat aus der Adresse (YYYY-MM), falls es keinen Monatsdatensatz gibt */
  month?: string | null;
  /** Sprache des Belegs (Ausfallhonorar-Zeile DE/EN); ohne Angabe deutsch */
  lang?: "de" | "en";
}

/**
 * Prüf-Chat Nachtest (03.11.2026), D: Ausfallmonat: die Position nennt das Ausfallhonorar mit Anteil und vereinbartem Honorar
 * („Ausfallhonorar (50 % von 400,00 €) · …“ bzw. „Cancellation fee (50% of €400.00) · …“), sonst unverändert.
 */
export function ausfallText(p: Pick<PeriodLike, "amount_cents" | "cancellation_fee_cents" | "original_amount_cents"> | null, text: string, currency: string, lang: "de" | "en" = "de"): string {
  const fee = p?.cancellation_fee_cents ?? 0;
  if (!p || fee <= 0) return text;
  const orig = p.original_amount_cents ?? 0;
  const loc = lang === "en" ? "en-GB" : orgLocale();
  const geld = (c: number) => geldAnzeige(c, currency, loc);
  const anteil = orig > 0 ? numberText(Math.round((fee / orig) * 10000) / 100, 2, loc) : null;
  const vorne = lang === "en"
    ? (anteil ? `Cancellation fee (${anteil}% of ${geld(orig)})` : "Cancellation fee")
    : (anteil ? `Ausfallhonorar (${anteil} % von ${geld(orig)})` : "Ausfallhonorar");
  return `${vorne} · ${text}`;
}

const pctText = (v: number) => `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(v)} %`;

/** Satz und Staffel der Kooperation im Abrechnungsmonat; im Nachlauf nach Austritt der Satz der Regel ohne Staffel (0074). */
export function satzDesMonats(deal: DealLike, month: string | null | undefined, talentCommissionBp: number | null, agencyBp: number | null | undefined): {
  percent: number; tiers: CommissionTier[] | null;
} {
  const nachlauf = deal.post_term && month ? dealBpForMonth({ ...deal, commission_bp: null }, month) : null;
  const dealBp = nachlauf ?? deal.commission_bp;
  const percent = resolveCommissionRate({
    deal: dealBp == null ? null : dealBp / 100,
    talent: talentCommissionBp == null ? null : talentCommissionBp / 100,
    agency: (agencyBp ?? 0) / 100,
  }).percent;
  return { percent, tiers: nachlauf != null ? null : tiersFromJson(deal.commission_tiers) };
}

/** Zusatzpositionen des Monats; ohne Monat alle der Kooperation. */
export function extrasDesMonats<I extends ItemLike>(deal: Pick<DealLike, "id">, period: Pick<PeriodLike, "id"> | null, periods: readonly Pick<PeriodLike, "id" | "month">[], items: readonly I[]): I[] {
  return items.filter((x) => x.deal_id === deal.id && (!period || extraInPeriod(x.period_id, period.id, periods)));
}

function agenturgebuehr(input: Pick<VorbelegungsInput, "terms" | "settings" | "period" | "periods">, amountCents: number, text: { percent: (pct: string) => string; fixed: string }): VorbelegungsZeile | null {
  const feeMode = input.terms?.agency_fee_mode ?? input.settings.agency_fee_mode;
  if (feeMode === "percent" && amountCents) {
    const bp = input.terms?.agency_fee_bp ?? input.settings.agency_fee_bp ?? 0;
    return bp > 0 ? { description: text.percent(pctText(bp / 100)), amountCents: Math.round((amountCents * bp) / 10000), commissionFree: true } : null;
  }
  if (feeMode === "fixed") {
    const c = input.terms?.agency_fee_cents ?? input.settings.agency_fee_cents ?? 0;
    const first = fixedFeePeriod(input.periods);
    // wie die Übersicht (overview.ts): die feste Gebühr trägt der erste nicht stornierte Monat mit Betrag, nie ein Monat ohne Honorar
    return c > 0 && amountCents > 0 && (!input.period || input.period.id === first?.id) ? { description: text.fixed, amountCents: c, commissionFree: true } : null;
  }
  return null;
}

/**
 * Monat, der die feste Agenturgebühr trägt (Gesamtprüfung 07.10.2026): der erste nicht stornierte Monat mit Betrag, wie die
 * Übersicht (packages/domain overview.ts, fixedFeeUsed). Vorher nahm die Vorbelegung den ersten Monat überhaupt; war der
 * storniert oder ohne Betrag, fehlte die Gebühr auf jeder Rechnung, während die Übersicht sie im nächsten Monat erwartete.
 */
export function fixedFeePeriod<P extends PeriodLike>(periods: readonly P[]): P | undefined {
  return [...periods].filter((p) => p.status !== "cancelled" && (p.amount_cents ?? 0) > 0).sort((a, b) => a.month.localeCompare(b.month))[0];
}

/** Minuszeile „Agenturvergütung der Marke“ auf der Rechnung an die Marke (0060): Abzug auf Honorar und provisionspflichtige Zusätze. */
function abzugsZeile(discount: BrandDiscount | null, honorarCents: number, suffix = ""): VorbelegungsZeile | null {
  const d = brandDiscountCents(honorarCents, discount);
  return d > 0 && discount ? { description: `Agenturvergütung der Marke · ${pctText(discount.percent)}${suffix}`, amountCents: -d, commissionFree: false } : null;
}

/** Positionen einer Einzelrechnung zu Kooperation und Monat. */
export function einzelVorbelegung(input: VorbelegungsInput): VorbelegungsZeile[] {
  const { deal, period: p, kind } = input;
  const month = p?.month ?? input.month ?? null;
  const extras = extrasDesMonats(deal, p, input.periods, input.items);
  const amount = p ? p.amount_cents : (deal.months && deal.months > 1 ? null : deal.budget_cents);
  const commissionOnly = deal.model === "commission_only";
  const when = p ? ` · ${monthLabel(p.month)}` : "";
  const leistung = ausfallText(p, `${deal.title}${when}${deal.deliverables ? ` · ${deal.deliverables}` : ""}`, deal.currency, input.lang);
  const discount = brandDiscountFromRow(deal);
  const rate = () => satzDesMonats(deal, month, input.talentCommissionBp, input.settings.default_commission_bp);
  const posten: VorbelegungsZeile[] = [
    ...(amount && !commissionOnly ? [{ description: leistung, amountCents: amount, commissionFree: false }] : []),
    ...extras.map((e) => ({ description: e.description, amountCents: e.amount_cents, commissionFree: !e.commissionable })),
  ];
  const honorar = posten.filter((l) => !l.commissionFree).reduce((a, l) => a + l.amountCents, 0);
  const out: VorbelegungsZeile[] = [];

  if (kind === "self_billing") {
    // Gutschrift: Anteil des Creators nach Satz oder Staffel und nach dem Abzug der Marke; durchlaufende Posten voll
    const r = rate();
    const share = creatorShareLines(posten.map((l, i) => ({ ...l, i })), r, discount);
    for (const l of share) {
      const orig = posten[l.i]!;
      out.push(l.commissionFree || !orig.amountCents ? { description: orig.description, amountCents: l.amountCents, commissionFree: l.commissionFree }
        : { description: `${orig.description} · Anteil ${pctText(Math.round((l.amountCents / orig.amountCents) * 10000) / 100)}`, amountCents: l.amountCents, commissionFree: false });
    }
    return out;
  }

  if (kind === "agency_to_talent") {
    // Provision der Agentur: Satz oder Staffel auf das Honorar nach dem Abzug der Marke (wie Aufteilung und Übersicht)
    const r = rate();
    if (honorar > 0) {
      const s = shareOfHonorar(honorar, r, discount);
      const satz = r.tiers ? `${pctText(Math.round(effectiveCommissionPercent(discount?.bearer === "agency" ? honorar : honorar - s.discountCents, r) * 100) / 100)} (Staffel)` : pctText(r.percent);
      const basis = discount && s.discountCents > 0
        ? discount.bearer === "agency"
          ? `${money(honorar, deal.currency)} abzüglich Agenturvergütung der Marke ${money(s.discountCents, deal.currency)}`
          : `${money(honorar - s.discountCents, deal.currency)} (nach Agenturvergütung der Marke ${pctText(discount.percent)})`
        : money(honorar, deal.currency);
      if (s.agencyCents > 0) out.push({ description: `Provision ${satz} auf ${basis} · ${deal.title}${when}`, amountCents: s.agencyCents, commissionFree: true });
    }
    const forecast = p?.commission_forecast_cents ?? null;
    if (forecast && (commissionOnly || deal.model === "fixed_plus_commission")) out.push({ description: `Provision · ${deal.title}${when}`, amountCents: forecast, commissionFree: true });
    return out;
  }

  if (kind === "talent_to_brand") {
    // Creator mit Vollmacht an die Marke: was die Marke zahlt (Honorar nach ihrem Abzug), wie die Vorschläge der Anforderung
    return brandNetLines(posten, discount);
  }

  // Rechnung der Agentur an die Marke
  // Praxistest K-161: Umsatzbeteiligung über die Agentur: die Agentur rechnet ihre Beteiligung mit der Marke ab. Vorbelegt mit der
  // erwarteten Provision des Monats; der echte Betrag folgt der Umsatzmeldung der Marke.
  if (!input.direct && commissionOnly) {
    const forecast = p?.commission_forecast_cents ?? null;
    if (forecast && forecast > 0) out.push({ description: `Umsatzbeteiligung · ${deal.title}${when} (laut Umsatzmeldung der Marke anpassen)`, amountCents: forecast, commissionFree: true });
  }
  if (!input.direct) {
    out.push(...posten);
    const abzug = abzugsZeile(discount, honorar);
    if (abzug) out.push(abzug);
  }
  // Im Direktweg stellt der Creator das Honorar; die Agentur rechnet mit der Marke nur ihre Gebühr ab (GELD-6)
  const fee = agenturgebuehr(input, commissionOnly ? 0 : amount ?? 0, { percent: (x) => `Agenturgebühr · ${x}`, fixed: "Agenturgebühr" });
  if (fee) out.push(fee);
  return out;
}

export interface SammelKandidat {
  deal: DealLike;
  period: PeriodLike;
  talent: string;
}

/** Positionen einer Sammelrechnung an die Marke: je Monat Honorar, Zusatzpositionen (erster Monat samt „Ganze Kooperation“), Abzug der Marke, Gebühr. */
export function sammelVorbelegung(gewaehlt: readonly SammelKandidat[], ctx: {
  periods: readonly (PeriodLike & { deal_id: string })[]; items: readonly ItemLike[]; terms: readonly (FeeLike & { deal_id: string })[]; settings: FeeLike;
}): (VorbelegungsZeile & { dealId: string; periodId: string })[] {
  const out: (VorbelegungsZeile & { dealId: string; periodId: string })[] = [];
  for (const k of gewaehlt) {
    const ref = { dealId: k.deal.id, periodId: k.period.id };
    const periods = ctx.periods.filter((x) => x.deal_id === k.deal.id);
    const when = monthLabel(k.period.month);
    const amount = k.period.amount_cents ?? 0;
    const extras = extrasDesMonats(k.deal, k.period, periods, ctx.items);
    const posten: VorbelegungsZeile[] = [
      { description: ausfallText(k.period, `${k.deal.title} · ${k.talent} · ${when}${k.deal.deliverables ? ` · ${k.deal.deliverables}` : ""}`, k.deal.currency), amountCents: amount, commissionFree: false },
      ...extras.map((e) => ({ description: `${e.description} · ${k.talent} · ${when}`, amountCents: e.amount_cents, commissionFree: !e.commissionable })),
    ];
    for (const l of posten) out.push({ ...l, ...ref });
    const honorar = posten.filter((l) => !l.commissionFree).reduce((a, l) => a + l.amountCents, 0);
    const abzug = abzugsZeile(brandDiscountFromRow(k.deal), honorar, ` · ${k.deal.title} · ${when}`);
    if (abzug) out.push({ ...abzug, ...ref });
    const fee = agenturgebuehr({ terms: ctx.terms.find((t) => t.deal_id === k.deal.id) ?? null, settings: ctx.settings, period: k.period, periods }, amount,
      { percent: (x) => `Agenturgebühr ${x} · ${k.deal.title} · ${when}`, fixed: `Agenturgebühr · ${k.deal.title}` });
    if (fee) out.push({ ...fee, ...ref });
  }
  return out;
}

/**
 * Zahlungsziel in Tagen für eine neue Rechnung (SAAS-R2-2). Gleiche Regel wie Festschreiben, Versand und Import (0087):
 *   Rechnung der Agentur an einen Creator   Forderung der Agentur: allgemeines Zahlungsziel (payment_terms_days)
 *   Gutschrift                              Die Agentur zahlt: Vorgabe für Creator-Rechnungen (talent_payment_terms_days)
 *   Rechnung an die Marke                   Kooperation, Marke, Vorgabe für Marken, allgemeines Zahlungsziel
 */
export function vorbelegtesZahlungsziel(art: VorbelegungsArt, s: {
  payment_terms_days?: number | null; talent_payment_terms_days?: number | null; brandPaymentTermDays?: number | null;
  dealTermsDays?: number | null; brandTermsDays?: number | null;
}): number {
  if (art === "agency_to_talent") return s.payment_terms_days ?? 14;
  if (art === "self_billing") return s.talent_payment_terms_days ?? s.payment_terms_days ?? 14;
  return s.dealTermsDays ?? s.brandTermsDays ?? s.brandPaymentTermDays ?? s.payment_terms_days ?? 14;
}

/**
 * Beschriftung einer Kooperation in der Auswahl „Rechnung erstellen“ (PRAXIS-R2-9): Marke · Titel · Nummer · Zeitraum, damit
 * wiederkehrende Kooperationen mit gleichem Titel unterscheidbar sind. Nummer wie in den Kooperationen („NT-D45“).
 */
export function kooperationsOption(d: { title: string; deal_number?: number | null; start_month?: string | null; months?: number | null },
  brandName: string, prefix: string | null | undefined): string {
  const code = d.deal_number == null ? null : prefix ? `${prefix}-D${d.deal_number}` : `D${d.deal_number}`;
  const start = d.start_month?.slice(0, 7) ?? null;
  const mm = (ym: string) => `${ym.slice(5, 7)}/${ym.slice(0, 4)}`;
  let span: string | null = null;
  if (start && /^\d{4}-\d{2}$/.test(start)) {
    const n = Math.max(1, d.months ?? 1);
    const endDate = new Date(Date.UTC(Number(start.slice(0, 4)), Number(start.slice(5, 7)) - 1 + n - 1, 1));
    const end = endDate.toISOString().slice(0, 7);
    span = end === start ? mm(start) : `${mm(start)}–${mm(end)}`;
  }
  return [brandName, d.title, code, span].filter((x) => !!x).join(" · ");
}

/** Reihenfolge in der Auswahl: laufende Kooperationen zuerst, abgeschlossene ans Ende; jeweils die neueste oben */
export function kooperationenSortiert<D extends { status: string; start_month?: string | null }>(deals: readonly D[]): D[] {
  const done = (d: D) => (d.status === "done" ? 1 : 0);
  return [...deals].sort((a, b) => done(a) - done(b) || (b.start_month ?? "").localeCompare(a.start_month ?? ""));
}

/**
 * Prüf-Chat Nachtest (03.11.2026), Befund 1: Stand eines Monats nach einem Storno. „ohne_honorar“: storniert ohne Honorar (der
 * Monat behält das vereinbarte Honorar nur für „Wieder öffnen“ und ist nicht abrechenbar, die Datenbank lehnt das Festschreiben ab,
 * 0497); „ausfall“: Ausfallhonorar statt des vereinbarten Honorars (abrechenbar, mit Rückfrage vor dem Festschreiben).
 */
export function stornoStand(p: { status: string; outcome?: string | null; cancellation_fee_cents?: number | null } | null | undefined): "ohne_honorar" | "ausfall" | null {
  if (!p) return null;
  if (p.status === "cancelled" && !(p.cancellation_fee_cents ?? 0)) return "ohne_honorar";
  if (p.outcome === "cancellation_fee" || p.outcome === "not_accepted_fee") return "ausfall";
  return null;
}
